import type { CSSProperties, ReactNode } from 'react';
import { createGlobalStyle } from 'styled-components';

export type ReviewDocumentColumnCount = 3 | 4 | 5 | 6;

export const ReviewDocumentChromeStyles = createGlobalStyle`
  .review-document-root {
    display: flex;
    flex-direction: column;
    min-width: 0;
    height: 100%;
    background: var(--color-drawer-bg, var(--color-layout-bg, #eaf6f7));
  }

  .review-document-split {
    display: flex;
    flex-direction: row;
    gap: 24px;
    padding: 0 4px;
    box-sizing: border-box;
    min-width: 0;
    min-height: 0;
    align-items: flex-start;
    flex: 1;
  }

  .review-document-viewer {
    flex: 0 0 540px;
    min-width: 0;
    min-height: 500px;
    max-height: calc(100vh - 132px);
    display: flex;
    flex-direction: column;
    align-self: flex-start;
  }

  .review-document-form {
    flex: 1;
    min-width: 0;
    min-height: 0;
    max-height: calc(100vh - 132px);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-sizing: border-box;
    padding: 8px 4px 12px;
    gap: 14px;
  }

  .review-document-root.is-compact .review-document-form {
    padding: 4px 4px 10px;
    gap: 10px;
  }

  .review-document-segmented {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    min-width: 0;
    margin-bottom: 10px;
    flex-shrink: 0;
  }

  .review-document-root.is-compact .review-document-segmented {
    margin-bottom: 6px;
  }

  .review-document-segmented .ant-segmented {
    padding: 2px !important;
    background: var(--color-review-segmented-track, rgba(255, 255, 255, 0.96)) !important;
    border: 1px solid var(--color-review-document-surface-border, rgba(219, 234, 227, 0.95)) !important;
    border-radius: 10px !important;
    box-shadow: 0 1px 2px rgba(4, 42, 42, 0.05);
  }

  .review-document-segmented .ant-segmented .ant-segmented-item-label {
    font-size: 11px !important;
    font-weight: 600 !important;
    line-height: 26px !important;
    padding: 0 11px !important;
    min-height: 26px !important;
    color: var(--color-segmented-item, #5a6a6a) !important;
  }

  .review-document-segmented
    .ant-segmented
    .ant-segmented-item-selected:not(.ant-segmented-item-disabled) {
    background-color: var(--color-primary, #41aaaf) !important;
    border-radius: 8px !important;
    box-shadow: 0 1px 3px color-mix(in srgb, var(--color-primary, #41aaaf) 32%, transparent) !important;
  }

  .review-document-segmented
    .ant-segmented
    .ant-segmented-item-selected:not(.ant-segmented-item-disabled)
    .ant-segmented-item-label {
    color: var(--color-review-segmented-selected-fg, #ffffff) !important;
    font-weight: 600 !important;
  }

  .review-document-segmented .ant-segmented .ant-segmented-thumb {
    background-color: transparent !important;
    box-shadow: none !important;
  }

  .review-document-segmented
    .ant-segmented
    .ant-segmented-item:not(.ant-segmented-item-selected):not(
      .ant-segmented-item-disabled
    ):hover,
  .review-document-segmented
    .ant-segmented
    .ant-segmented-item:not(.ant-segmented-item-selected):not(
      .ant-segmented-item-disabled
    ):hover
    .ant-segmented-item-label {
    color: var(--color-text-dark, #042a2a) !important;
  }

  .review-document-segmented .ant-segmented .ant-segmented-item:focus-visible {
    outline: 2px solid color-mix(in srgb, var(--color-primary, #41aaaf) 45%, transparent);
    outline-offset: 1px;
  }

  .review-document-scroll {
    flex: 1;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    padding-right: 4px;
    position: relative;
    scrollbar-width: thin;
    scrollbar-color: var(--color-border, #dbeae3) transparent;
  }

  .review-document-scroll::-webkit-scrollbar {
    width: 6px;
  }

  .review-document-scroll::-webkit-scrollbar-thumb {
    background: var(--color-border, #dbeae3);
    border-radius: 3px;
  }

  .review-document-scroll.with-delete-gutter {
    padding-right: 30px;
  }

  .review-document-card {
    margin-bottom: 18px;
    padding: 14px 16px;
    border-radius: 8px;
    border: 1px solid var(--color-review-document-surface-border, rgba(219, 234, 227, 0.95));
    background: var(--color-review-document-surface, rgba(255, 255, 255, 0.72));
  }

  .review-document-root.is-compact .review-document-card {
    margin-bottom: 14px;
    padding: 10px 12px;
  }

  .review-document-card-heading {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 4px 16px;
    margin-bottom: 12px;
    font-size: 12px;
    line-height: 1.5;
    color: var(--color-text-dark, #042a2a);
  }

  .review-document-root.is-compact .review-document-card-heading {
    margin-bottom: 8px;
    font-size: 11px;
  }

  .review-document-card-title {
    font-weight: 600;
    color: var(--color-text-dark, #042a2a);
  }

  .review-document-card-totals {
    color: var(--color-text-muted, #5a6a6a);
    font-weight: 400;
  }

  .review-document-line-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin: 4px 0 8px;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.4;
    color: var(--color-primary, #41aaaf);
  }

  .review-document-root.is-compact .review-document-line-heading {
    font-size: 11px;
    margin-bottom: 6px;
  }

  .review-document-grid {
    display: grid;
    width: 100%;
    column-gap: 24px;
    row-gap: 10px;
    align-items: start;
  }

  .review-document-field {
    padding: 7px 6px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 5px;
    border-radius: 10px;
    min-height: 0;
    min-width: 0;
    max-width: 100%;
    list-style: none;
    transition: background-color 0.15s ease;
  }

  .review-document-root.is-compact .review-document-field {
    padding: 5px 4px;
    gap: 3px;
  }

  .review-document-field:hover {
    background-color: var(--color-review-field-hover, #d4edef);
  }

  /* Same chrome as generate.TAX EditDrawerControlWrap: 12px/600, 33px, square borders. */
  .review-document-field .ant-input,
  .review-document-field .ant-picker,
  .review-document-root .transaction-edit-drawer-lines-table .ant-input,
  .review-document-root .transaction-edit-drawer-lines-table .ant-picker {
    font-family: var(--font-family, Inter, sans-serif) !important;
    font-size: 12px !important;
    font-weight: 600 !important;
    min-height: 33px !important;
    height: 33px !important;
    border: 1px solid var(--color-border, #dbeae3) !important;
    border-radius: 0;
    box-sizing: border-box !important;
  }

  .review-document-field .ant-input {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .review-document-field .ant-picker .ant-picker-input,
  .review-document-root .transaction-edit-drawer-lines-table .ant-picker .ant-picker-input {
    height: 100%;
  }

  .review-document-field .ant-picker .ant-picker-input > input,
  .review-document-root .transaction-edit-drawer-lines-table .ant-picker .ant-picker-input > input {
    font-family: var(--font-family, Inter, sans-serif) !important;
    font-size: 12px !important;
    font-weight: 600 !important;
  }

  .review-document-field .ant-picker-suffix,
  .review-document-field .ant-picker-suffix .anticon,
  .review-document-field .ant-picker-clear,
  .review-document-field .ant-picker-clear .anticon,
  .review-document-root .transaction-edit-drawer-lines-table .ant-picker-suffix,
  .review-document-root .transaction-edit-drawer-lines-table .ant-picker-suffix .anticon {
    color: var(--color-primary, #41aaaf) !important;
  }

  .review-document-field .ant-select,
  .review-document-root .transaction-edit-drawer-lines-table .ant-select {
    min-height: 33px !important;
    height: 33px !important;
    width: 100%;
    border: none !important;
    box-shadow: none !important;
    box-sizing: border-box !important;
  }

  .review-document-field .ant-select .ant-select-selector,
  .review-document-field .scope-form-select.ant-select .ant-select-selector,
  .review-document-root .transaction-edit-drawer-lines-table .ant-select .ant-select-selector,
  .review-document-root .transaction-edit-drawer-lines-table .scope-form-select.ant-select .ant-select-selector {
    font-family: var(--font-family, Inter, sans-serif) !important;
    font-size: 12px !important;
    font-weight: 600 !important;
    min-height: 33px !important;
    height: 33px !important;
    border: 1px solid var(--color-border, #dbeae3) !important;
    border-radius: 0;
    box-sizing: border-box !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    display: flex !important;
    align-items: center !important;
  }

  .review-document-field .scope-form-select.ant-select {
    border: 1px solid transparent !important;
    box-shadow: none !important;
  }

  .review-document-field .ant-select:hover,
  .review-document-field .ant-select.ant-select-focused,
  .review-document-field .ant-select.ant-select-open,
  .review-document-field .scope-form-select.ant-select:hover,
  .review-document-field .scope-form-select.ant-select.ant-select-focused,
  .review-document-field .scope-form-select.ant-select.ant-select-open {
    /* Keep 1px so hover does not drop the transparent wrapper border (layout jump). */
    border: 1px solid transparent !important;
    box-shadow: none !important;
  }

  .review-document-field .ant-select:hover .ant-select-selector,
  .review-document-field .ant-select.ant-select-focused .ant-select-selector,
  .review-document-field .ant-select.ant-select-open .ant-select-selector,
  .review-document-field .scope-form-select.ant-select:hover .ant-select-selector,
  .review-document-field .scope-form-select.ant-select.ant-select-focused .ant-select-selector,
  .review-document-field .scope-form-select.ant-select.ant-select-open .ant-select-selector {
    border-color: var(--color-border, #dbeae3) !important;
    box-shadow: none !important;
  }

  .review-document-field .ant-select-selection-item,
  .review-document-field .ant-select-selection-placeholder,
  .review-document-field .ant-select-selection-search-input {
    line-height: 20px !important;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
    font-weight: 600 !important;
  }

  .review-document-field .ant-select .ant-select-arrow,
  .review-document-root .transaction-edit-drawer-lines-table .ant-select .ant-select-arrow {
    color: var(--color-primary, #41aaaf) !important;
  }

  .review-document-field .ant-input[readonly],
  .review-document-field .ant-input[readonly]:hover,
  .review-document-field .ant-input[readonly]:focus,
  .review-document-root .transaction-edit-drawer-lines-table .ant-input[readonly] {
    background-color: var(--color-review-readonly-bg, #f5f5f5) !important;
    cursor: default !important;
    color: var(--color-text-base, #2e2e2e) !important;
    opacity: 1 !important;
  }

  .review-document-select-dropdown.ant-select-dropdown {
    z-index: 1300 !important;
  }

  .review-document-select-dropdown.ant-select-dropdown .ant-select-item {
    font-family: var(--font-family, Inter, sans-serif) !important;
    font-size: 12px !important;
    font-weight: 600 !important;
  }

  .review-document-field-label {
    font-family: var(--font-family, Inter, sans-serif);
    font-size: 11px;
    font-weight: 600;
    line-height: 14px;
    color: var(--color-text-muted, #5a6a6a);
    display: flex;
    align-items: center;
    gap: 4px;
    width: 100%;
    min-width: 0;
    min-height: 20px;
  }

  .review-document-root.is-compact .review-document-field-label {
    font-size: 10px;
    min-height: 20px;
  }

  .review-document-field-label > span:first-child {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }

  .review-document-field-label-trail {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    gap: 6px;
    margin-left: auto;
  }

  .review-document-saving {
    position: absolute;
    inset: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--color-bg-mask, rgba(0, 0, 0, 0.45));
    border-radius: 8px;
  }

  .review-document-switch {
    display: inline-flex;
    align-self: flex-start;
    width: auto;
    flex: none;
  }

  .review-document-switch .ant-switch {
    width: auto;
    min-width: 28px;
    flex: none;
  }
`;

export const ReviewDocumentRoot = ({
  compact,
  children,
}: {
  compact?: boolean;
  children: ReactNode;
}) => (
  <div
    className={`review-document-root drawer-panel-root${compact ? ' is-compact' : ''}`}
  >
    <ReviewDocumentChromeStyles />
    {children}
  </div>
);

export const ReviewDocumentSplit = ({ children }: { children: ReactNode }) => (
  <div className="review-document-split">{children}</div>
);

export const ReviewDocumentViewerPane = ({
  children,
}: {
  children: ReactNode;
}) => <div className="review-document-viewer">{children}</div>;

export const ReviewDocumentFormPane = ({
  children,
}: {
  children: ReactNode;
}) => <div className="review-document-form">{children}</div>;

export const ReviewDocumentSegmentedWrap = ({
  children,
}: {
  children: ReactNode;
}) => <div className="review-document-segmented">{children}</div>;

export const ReviewDocumentLinesScroll = ({
  children,
  withDeleteGutter,
}: {
  children: ReactNode;
  withDeleteGutter?: boolean;
}) => (
  <div
    className={`review-document-scroll${withDeleteGutter ? ' with-delete-gutter' : ''}`}
  >
    {children}
  </div>
);

export const ReviewDocumentDetailsCard = ({
  title,
  totals,
  children,
}: {
  title: ReactNode;
  totals?: ReactNode;
  children: ReactNode;
}) => (
  <div className="review-document-card">
    <div className="review-document-card-heading">
      <span className="review-document-card-title">{title}</span>
      {totals ? (
        <span className="review-document-card-totals">{totals}</span>
      ) : null}
    </div>
    {children}
  </div>
);

export const ReviewDocumentLineHeading = ({
  children,
  extra,
}: {
  children: ReactNode;
  extra?: ReactNode;
}) => (
  <div className="review-document-line-heading">
    <span>{children}</span>
    {extra}
  </div>
);

export const ReviewDocumentFieldGrid = ({
  columns,
  children,
}: {
  columns: ReviewDocumentColumnCount;
  children: ReactNode;
}) => (
  <div
    className="review-document-grid"
    style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
  >
    {children}
  </div>
);

export const ReviewDocumentField = ({
  label,
  hint,
  trail,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  trail?: ReactNode;
  children: ReactNode;
}) => (
  <div className="review-document-field">
    <div className="review-document-field-label">
      <span>{label}</span>
      {hint}
      {trail ? (
        <span className="review-document-field-label-trail">{trail}</span>
      ) : null}
    </div>
    {children}
  </div>
);

export const ReviewDocumentSavingOverlay = ({
  show,
  style,
  children,
}: {
  show: boolean;
  style?: CSSProperties;
  children?: ReactNode;
}) =>
  show ? (
    <div className="review-document-saving" style={style} aria-busy aria-live="polite">
      {children}
    </div>
  ) : null;
